import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUserState } from "#core/state/UserState.ts";
import { hasProxySessionCookies } from "#core/auth/cookieAuth.ts";
import { safeInAppPath } from "#core/auth/enter-app.ts";
import { LoginCard } from "#components/entities/users/login/login-card.tsx";

export const Route = createFileRoute("/_authentication/login")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: safeInAppPath(search.redirect),
  }),
});

function RouteComponent() {
  const { t } = useTranslation("auth");
  const { redirect } = Route.useSearch();
  const userState = useUserState(["loginState"]);
  const router = useRouter();
  const destination = redirect ?? "/";

  // Never show the form to someone who already has a session — they'd fill it in for a
  // second time or, arriving from the v1 proxy hand-off (which signs them in via cookies
  // and can land them here), sit on it forever. `history.replace` rather than <Navigate>
  // because the destination is an arbitrary href that may carry a query string.
  const signedIn = userState.loginState === "success";
  useEffect(() => {
    if (signedIn) router.history.replace(destination);
  }, [signedIn, destination, router]);

  // The proxy hand-off leaves its tokens in cookies, so a session may still be one probe
  // away (`bootstrapSessionFromProxyCookies`); wait for it rather than flashing the form.
  const awaitingProxySession = userState.loginState === "pending" && hasProxySessionCookies();

  if (signedIn || awaitingProxySession)
    return <div className="ynt-fullscreen-center text-body-secondary">{t("loggingIn")}</div>;

  return <LoginCard destination={destination} />;
}
